import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import FormCostiEventi from '../../../../components/FormCostiEventi';
import TitlePage from '../../../../components/TitlePage';

import { useParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/Auth';
import { EventAttributes, EventRequest } from '../../../../interfaces/events';
import { ParticipantStatus } from '../../../../interfaces/participants';
import {
  Balance,
  HeadquarterBalance,
} from '../../../../interfaces/strapiComponents/balance';
import {
  isCourseCostsFormEnabled,
  isEventBalanceEnabledForEdit,
} from '../../../../libs/utils/helpers';
import sectionsAPIs from '../../../../api/section';
import { useQuery } from 'react-query';
import {
  calculateRequiredBottles,
  normalizeGlassesPerBottle,
} from '../../utils/financialValidation';

interface Props {
  defaultEvent: EventAttributes | undefined;
}

const CostiEvento: React.FC<Props> = ({ defaultEvent }) => {
  const { watch, setValue, getValues, control } = useFormContext<EventRequest>();
  const { eventId } = useParams();

  const [{ token, profile }] = useAuth();

  // Ottieni l'ID della sezione selezionata nel form
  const selectedSectionId = watch('section');
  
  // Query per ottenere i dati della sezione quando cambia
  // SOLO durante la creazione di un nuovo evento (quando defaultEvent è undefined)
  const { data: sectionData } = useQuery({
    queryKey: ['section', selectedSectionId],
    queryFn: () => sectionsAPIs.findOne({ 
      id: selectedSectionId, 
      token,
      query: { populate: '*' }
    }),
    enabled: !!selectedSectionId && !!token && !defaultEvent, // Solo se non c'è defaultEvent (creazione)
  });

  // Calcola l'IBAN dalla sezione selezionata o dal defaultEvent
  const sectionIban = useMemo(() => {
    // Se abbiamo un defaultEvent (modifica evento), usa quello
    if (defaultEvent?.section?.data?.attributes?.iban) {
      return defaultEvent.section.data.attributes.iban;
    }
    
    // Altrimenti, usa i dati della sezione ottenuti dalla query (creazione nuovo evento)
    if (sectionData?.data?.attributes?.iban) {
      return sectionData.data.attributes.iban;
    }
    
    return '';
  }, [defaultEvent?.section?.data?.attributes?.iban, sectionData?.data?.attributes?.iban]);

  const lessons = 1; // ??
  const partecipations = watch('partecipations');

  // Numero partecipanti CONFERMATI: confermati + gratis + conviventi (solo CONFIRMED, ciascuno 1 + guests)
  const totalConfirmedParticipants = useMemo(() => {
    const data = partecipations?.data;
    if (!Array.isArray(data)) return 0;
    return data
      .filter((elem) => elem?.attributes?.status === ParticipantStatus.CONFIRMED)
      .reduce(
        (acc, elem) =>
          acc + 1 + (elem?.attributes?.guests?.length ?? 0),
        0
      );
  }, [partecipations]);

  // Per controllo max partecipanti: confermati + gratis + conviventi + in attesa (CONFIRMED + PENDING, ciascuno 1 + guests)
  const totalConfirmedAndPendingParticipants = useMemo(() => {
    const data = partecipations?.data;
    if (!Array.isArray(data)) return 0;
    return data
      .filter(
        (elem) =>
          elem?.attributes?.status === ParticipantStatus.CONFIRMED ||
          elem?.attributes?.status === ParticipantStatus.PENDING
      )
      .reduce(
        (acc, elem) =>
          acc + 1 + (elem?.attributes?.guests?.length ?? 0),
        0
      );
  }, [partecipations]);

  const minParticipants = useWatch({ control, name: 'minParticipants' }) || 0;
  const subscriptionAmount = useWatch({ control, name: 'subscriptionAmount' });
  const wines = useWatch({ control, name: 'wines' }) || [];
  const toNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const balance = watch('balance') || {};
  const balanceVoiceKeys = Object.keys(balance) as unknown as (keyof Balance)[];

  const headquarterBalance = watch('headquarterBalance') || {};
  const headquarterBalanceVoiceKeys = Object.keys(
    headquarterBalance
  ) as unknown as (keyof HeadquarterBalance)[];

  const getEstimatedOutflow = (voicesToSkip?: string[]) => {
    const estimatedOutflows = balanceVoiceKeys.reduce(
      (estimatedOutflows, balanceVoice) => {
        const skipVoice =
          voicesToSkip?.indexOf(balanceVoice)! > -1 || balanceVoice === 'id';
        if (skipVoice) return estimatedOutflows;
        return estimatedOutflows + toNumber(balance[balanceVoice]?.estimatedOutflow);
      },
      0
    );
    return estimatedOutflows;
  };

  const getEstimatedIncome = () => {
    const estimatedIncomes = balanceVoiceKeys.reduce(
      (estimatedIncomes, balanceVoice) => {
        if (balanceVoice === 'id') return estimatedIncomes;
        return estimatedIncomes + toNumber(balance[balanceVoice]?.estimatedIncome);
      },
      0
    );
    return estimatedIncomes;
  };

  const getFinalOutflow = (voicesToSkip?: string[]) => {
    const finalOutflows = balanceVoiceKeys.reduce(
      (finalOutflows, balanceVoice) => {
        const skipVoice =
          voicesToSkip?.indexOf(balanceVoice)! > -1 || balanceVoice === 'id';
        if (skipVoice) return finalOutflows;
        return finalOutflows + toNumber(balance[balanceVoice]?.finalOutflow);
      },
      0
    );

    return finalOutflows;
  };

  const getFinalIncome = () => {
    const finalIncomes = balanceVoiceKeys.reduce(
      (finalIncomes, balanceVoice) => {
        if (balanceVoice === 'id') return finalIncomes;
        return finalIncomes + toNumber(balance[balanceVoice]?.finalIncome);
      },
      0
    );
    return finalIncomes;
  };

  const getEstimatedWinesOutflow = () => {
    const estimatedWinesOutflow = wines.reduce(
      (estimatedWinesOutflow, wine) => {
        const estimatedTotal =
          wine.estimatedPricePerBottle * wine.estimatedBottles;
        return estimatedWinesOutflow + estimatedTotal;
      },
      0
    );
    return estimatedWinesOutflow;
  };

  const getFinalWinesOutflow = () => {
    const finalWinesOutflow = wines.reduce((finalWinesOutflow, wine) => {
      const finalTotal = wine.finalPricePerBottle * wine.finalBottles;
      return finalWinesOutflow + finalTotal;
    }, 0);
    return finalWinesOutflow;
  };

  const getEstimatedHeadquarterOutflow = () => {
    const estimatedOutflow = headquarterBalanceVoiceKeys.reduce(
      (estimatedOutflow, balanceVoice) => {
        if (balanceVoice === 'id') return estimatedOutflow;
        if (balanceVoice === 'administrations') {
          return (
            estimatedOutflow +
            toNumber(headquarterBalance[balanceVoice]?.estimatedIncome)
          );
        }
        return estimatedOutflow + toNumber(headquarterBalance[balanceVoice]?.estimatedOutflow);
      },
      0
    );

    return estimatedOutflow;
  };

  const getFinalHeadquarterOutflow = () => {
    const finalOutflow = headquarterBalanceVoiceKeys.reduce(
      (finalIncomes, balanceVoice) => {
        if (balanceVoice === 'id') return finalIncomes;
        if (balanceVoice === 'administrations') {
          return (
            finalIncomes +
            toNumber(headquarterBalance[balanceVoice]?.finalIncome)
          );
        }
        return finalIncomes + toNumber(headquarterBalance[balanceVoice]?.finalOutflow);
      },
      0
    );
    return finalOutflow;
  };

  const updatePriceVoice = () => {
    const subscriptionAmountWithoutAdministrativeAmount =
      toNumber(subscriptionAmount) -
      toNumber(watch('headquarterBalance.administrations.estimatedPriceUnit'));
    const updatedEstimatedIncome = subscriptionAmountWithoutAdministrativeAmount * minParticipants;
    setValue('balance.price.estimatedPriceUnit', subscriptionAmountWithoutAdministrativeAmount);
    setValue('balance.price.estimatedIncome', updatedEstimatedIncome);

    setValue('balance.price.finalPriceUnit', subscriptionAmountWithoutAdministrativeAmount);
    if (!partecipations?.data.length) return;
  };

  const updateFoodVoice = () => {
    const food = getValues('balance.food');
    const updatedEstimatedOutflow = food?.estimatedPriceUnit * minParticipants;
    setValue('balance.food.estimatedOutflow', updatedEstimatedOutflow);

    const confirmedParticipants = watch('confirmedParticipants');

    if (!confirmedParticipants) return;

    const updatedFinalPriceUnit = food?.finalOutflow / confirmedParticipants;
    setValue('balance.food.finalPriceUnit', updatedFinalPriceUnit);
  };

  const updateRoomsVoice = () => {
    const rooms = getValues('balance.rooms');

    const updatedEstimatedPriceUnit = rooms.estimatedOutflow / lessons;
    setValue('balance.rooms.estimatedPriceUnit', updatedEstimatedPriceUnit);

    const updatedFinalPriceUnit = rooms.finalOutflow / lessons;
    setValue('balance.rooms.finalPriceUnit', updatedFinalPriceUnit);
  };

  const updateSponsorshipVoice = () => {
    const sponsorship = getValues('balance.sponsorship');

    const updatedEstimatedPriceUnit =
      sponsorship.estimatedOutflow / minParticipants;
    setValue(
      'balance.sponsorship.estimatedPriceUnit',
      updatedEstimatedPriceUnit
    );

    if (!partecipations?.data.length) return;
    const updatedFinalPriceUnit =
      sponsorship.finalOutflow / partecipations.data.length;

    setValue('balance.sponsorship.finalPriceUnit', updatedFinalPriceUnit);
  };

  const updateExtraVoice = () => {
    const estimatedExtraOutflow = getEstimatedOutflow(['extras']) * 0.1;

    setValue('balance.extras.estimatedOutflow', estimatedExtraOutflow);
  };

  const updateTeacherPaymentsVoice = () => {
    const teacherPayments = watch('balance.teacherPayments');

    const estimatedTeacherPaymentsPriceUnit =
      teacherPayments.estimatedOutflow / minParticipants;

    setValue(
      'balance.teacherPayments.estimatedPriceUnit',
      estimatedTeacherPaymentsPriceUnit
    );
    // const finalTeacherPaymentsPriceUnit =
    //   teacherPayments.finalOutflow / partecipations.length;
    //   setValue(
    //     'balance.teacherPayments.finalPriceUnit',
    // finalTeacherPaymentsPriceUnit
    // );
  };

  const updateTeacherRefundsVoice = () => {
    const teacherRefunds = watch('balance.teacherRefunds');

    const estimatedTeacherRefundsPriceUnit =
      teacherRefunds.estimatedOutflow / minParticipants;
    setValue(
      'balance.teacherRefunds.estimatedPriceUnit',
      estimatedTeacherRefundsPriceUnit
    );

    // const finalTeacherRefundsPriceUnit =
    //   teacherRefunds.finalOutflow / partecipations.length;
    // setValue(
    //   'balance.teacherRefunds.finalPriceUnit',
    //   finalTeacherRefundsPriceUnit
    // );
  };

  const updateTeachingMaterialVoice = () => {
    const teachingMaterials = getValues('headquarterBalance.teachingMaterial');
    const totalParticipants = watch('confirmedParticipants');
    const defaultTeachingMaterialsBalance =
      defaultEvent?.headquarterBalance?.teachingMaterial;

    /**
     * SOLO IN MODIFICA
     *
     * Se l'utente modifica il valore delle entrate della quota material didattico,
     * allora bisogna segnarlo attraverso l'attributo isTouched
     *
     * Questo servirà poi quando si sincronizza il bilancio.
     *
     * Se è stato toccato significa che non verrà più calcolato automaticamente.
     */

    if (defaultTeachingMaterialsBalance) {
      const { isTouched, finalOutflow: defaultFinalOutflow } =
        defaultTeachingMaterialsBalance;

      if (
        !isTouched &&
        defaultFinalOutflow !== teachingMaterials.finalOutflow
      ) {
        setValue('headquarterBalance.teachingMaterial.isTouched', true);
      } else if (!isTouched) {
        setValue('headquarterBalance.teachingMaterial.isTouched', false);
      }
    }

    const estimatedOutflow =
      teachingMaterials?.estimatedPriceUnit * minParticipants;
    const finalPriceUnit = totalParticipants
      ? teachingMaterials?.finalOutflow / totalParticipants
      : 0;

    setValue(
      'headquarterBalance.teachingMaterial.estimatedOutflow',
      estimatedOutflow
    );
    setValue(
      'headquarterBalance.teachingMaterial.finalPriceUnit',
      finalPriceUnit
    );
  };

  const updateWinesVoice = () => {
    const estimatedWinesOutflow = getEstimatedWinesOutflow();
    const finalWinesOutflow = getFinalWinesOutflow();

    const estimatedBottles = wines.reduce((bottles, wine) => {
      return bottles + wine.estimatedBottles;
    }, 0);

    const finalBottles = wines.reduce((bottles, wine) => {
      return bottles + wine.finalBottles;
    }, 0);

    const estimatedWinesPriceUnit = estimatedBottles
      ? estimatedWinesOutflow / estimatedBottles
      : 0;

    setValue('balance.wines.estimatedPriceUnit', estimatedWinesPriceUnit);
    setValue('balance.wines.estimatedOutflow', estimatedWinesOutflow);

    if (!partecipations?.data.length) return;
    const finalWinesPriceUnit = finalBottles
      ? finalWinesOutflow / finalBottles
      : 0;
    setValue('balance.wines.finalPriceUnit', finalWinesPriceUnit);
    setValue('balance.wines.finalOutflow', finalWinesOutflow);
  };

  const syncWineRows = () => {
    const currentWines = getValues('wines') || [];

    currentWines.forEach((wine, wineIdx) => {
      const estimatedBottles = calculateRequiredBottles(
        minParticipants,
        wine.estimatedGlassesPerBottle,
      );
      const finalBottles = calculateRequiredBottles(
        totalConfirmedParticipants,
        wine.finalGlassesPerBottle,
      );

      if (toNumber(wine.estimatedBottles) !== estimatedBottles) {
        setValue(`wines.${wineIdx}.estimatedBottles` as const, estimatedBottles);
      }

      if (toNumber(wine.finalBottles) !== finalBottles) {
        setValue(`wines.${wineIdx}.finalBottles` as const, finalBottles);
      }
    });
  };

  const watchedValues = watch([
    'minParticipants',
    'subscriptionAmount',
    'partecipations',

    'balance.food.estimatedPriceUnit',
    'balance.food.finalOutflow',

    'balance.rooms.estimatedOutflow',
    'balance.rooms.finalOutflow',

    'balance.contributes.estimatedIncome',
    'balance.contributes.finalIncome',

    'balance.couponsExpenses.estimatedOutflow',
    'balance.couponsExpenses.finalOutflow',

    'balance.discountExpenses.estimatedOutflow',
    'balance.discountExpenses.finalOutflow',

    'balance.sectionVariousExpenses.estimatedOutflow',
    'balance.sectionVariousExpenses.finalOutflow',

    'balance.sponsorship.estimatedOutflow',
    'balance.sponsorship.finalOutflow',

    'balance.teacherPayments.estimatedOutflow',
    'balance.teacherPayments.finalOutflow',

    'balance.teacherRefunds.estimatedOutflow',
    'balance.teacherRefunds.finalOutflow',

    'headquarterBalance.teachingMaterial.estimatedPriceUnit',
    'headquarterBalance.teachingMaterial.finalOutflow',

    'headquarterBalance.shipping.estimatedPriceUnit',
    'headquarterBalance.shipping.finalPriceUnit',

    'balance.wines.estimatedOutflow',
    'balance.wines.finalOutflow',
  ]);

  const estimatedWinesOutflow = getEstimatedWinesOutflow();
  const finalWinesOutflow = getFinalWinesOutflow();
  const winesSignature = useMemo(
    () =>
      JSON.stringify(
        wines.map((wine) => ({
          estimatedPricePerBottle: wine.estimatedPricePerBottle,
          estimatedBottles: wine.estimatedBottles,
          estimatedGlassesPerBottle: wine.estimatedGlassesPerBottle,
          finalPricePerBottle: wine.finalPricePerBottle,
          finalBottles: wine.finalBottles,
          finalGlassesPerBottle: wine.finalGlassesPerBottle,
        })),
      ),
    [wines],
  );

  const disableForDelegates = useMemo(
    () => !isCourseCostsFormEnabled(watch('status')),
    [watch('status')]
  );

  const disableEditConsuntivo = useMemo(
    () => !isEventBalanceEnabledForEdit(watch('status'), profile),
    [watch('status'), profile]
  );

  useEffect(() => {
    syncWineRows();
  }, [minParticipants, totalConfirmedParticipants, winesSignature]);

  useEffect(() => {
    updatePriceVoice();
    updateWinesVoice();
    updateFoodVoice();
    updateRoomsVoice();
    updateSponsorshipVoice();
    updateExtraVoice();
    updateTeacherPaymentsVoice();
    updateTeacherRefundsVoice();

    updateTeachingMaterialVoice();
  }, [
    partecipations,
    totalConfirmedParticipants,
    estimatedWinesOutflow,
    finalWinesOutflow,
    ...watchedValues,
  ]);

  return (
    <>
      <TitlePage title={`Dettaglio evento | Costi`} />
      <FormCostiEventi
        estimatedIncome={getEstimatedIncome()}
        estimatedOutflow={getEstimatedOutflow()}
        finalIncome={getFinalIncome()}
        finalOutflow={getFinalOutflow()}
        estimatedHeadquarterOutflow={getEstimatedHeadquarterOutflow()}
        finalHeadquarterOutflow={getFinalHeadquarterOutflow()}
        disabled={disableForDelegates}
        disableEditConsuntivo={disableEditConsuntivo}
        eventStatus={watch('status')}
        sectionIban={sectionIban}
        currentConfirmedAndPendingParticipants={totalConfirmedAndPendingParticipants}
        currentConfirmedParticipants={totalConfirmedParticipants}
        originalMinParticipants={defaultEvent?.minParticipants}
      />
    </>
  );
};

export default CostiEvento;
